import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { Loader2 } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const QuickAnswerSchema = Yup.object().shape({
	shortcut: Yup.string()
		.min(2, "Muito curto")
		.max(15, "Muito longo")
		.required("Campo obrigatório"),
	message: Yup.string()
		.min(8, "Muito curto")
		.max(30000, "Muito longo")
		.required("Campo obrigatório"),
});

const gerarShortcut = (mensagem) => {
	const base = (mensagem || "")
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[^a-z0-9 ]/g, " ")
		.trim()
		.split(/\s+/)
		.slice(0, 3)
		.join("-")
		.slice(0, 15)
		.replace(/^-|-$/g, "");
	return base.length >= 2 ? base : "resp";
};

const QuickAnswersModal = ({
	open,
	onClose,
	quickAnswerId,
	initialValues,
	onUse,
}) => {
	const isMounted = useRef(true);

	const initialState = {
		shortcut: "",
		message: "",
	};

	const [quickAnswer, setQuickAnswer] = useState(initialState);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		const fetchQuickAnswer = async () => {
			if (initialValues) {
				setQuickAnswer((prevState) => {
					return { ...prevState, ...initialValues };
				});
			}

			if (!quickAnswerId) return;

			try {
				const { data } = await api.get(`/quickAnswers/${quickAnswerId}`);
				if (isMounted.current) {
					setQuickAnswer(data);
				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchQuickAnswer();
	}, [quickAnswerId, open, initialValues]);

	const handleClose = () => {
		onClose();
		setQuickAnswer(initialState);
	};

	// "Aplicar" = usar a resposta agora (preencher no campo + anexo se tiver), sem
	// enviar nada sozinho. É o que era o comportamento do chip antes, mas agora
	// de dentro do modal, com o texto do textarea editado no momento (não salva
	// nada no banco, só usa os valores alterados).
	// Obs: mediaUrl/mediaName vem da resposta original (initialValues / quickAnswer)
	// porque por aqui só permite editar o texto, não mexe com anexo, e NUNCA move
	// cartão no Kanban (foi pedido para não aparecer / não fazer).
	const handleUsarAgora = (values) => {
		if (!onUse) return;
		try {
			onUse({
				...(initialValues || {}),
				...(quickAnswer || {}),
				...values,
				id: quickAnswerId || initialValues?.id || null,
				shortcut: values.shortcut || quickAnswer?.shortcut || initialValues?.shortcut || "resp",
				message: values.message,
				mediaUrl: initialValues?.mediaUrl || quickAnswer?.mediaUrl || null,
				mediaName: initialValues?.mediaName || quickAnswer?.mediaName || null,
				moveKanbanColumnId: null,
			});
		} catch (_) { /* ignora */ }
		handleClose();
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>
						{quickAnswerId ? "Editar e usar resposta" : "Escrever e usar resposta"}
					</DialogTitle>
					<DialogDescription>
						Ajuste o texto da resposta rápida antes de usar. A alteração não é
						salva: vale só para esta mensagem.
					</DialogDescription>
				</DialogHeader>
				<Formik
					initialValues={quickAnswer}
					enableReinitialize={true}
					validationSchema={QuickAnswerSchema}
					onSubmit={async (values, actions) => {
						// Não há submit persistente (usuário não quer "Salvar alterações").
						// Enter no formulário cai direto no "Usar resposta" (comportamento
						// desejado, pois a ação principal é só usar e fechar).
						const vals = {
							...values,
							shortcut:
								values.shortcut && values.shortcut.trim().length >= 2
									? values.shortcut
									: gerarShortcut(values.message),
						};
						handleUsarAgora(vals);
						actions.setSubmitting(false);
					}}
				>
					{({ errors, touched, isSubmitting, setFieldValue, getValues }) => (
						<Form className="space-y-4">
							{/* Campo Atalho escondido — o usuário não vê, mas o schema
							    do Formik precisa dele. Preenchido apenas na hora de usar. */}
							<Field
								as={Input}
								id="shortcut"
								name="shortcut"
								className="hidden"
								tabIndex={-1}
								aria-hidden="true"
							/>

							<div className="space-y-1.5">
								<Label htmlFor="message">
									{i18n.t("quickAnswersModal.form.message")}
								</Label>
								{/* Usar render-prop do Field (não "as={Textarea} com onChange
								    custom) — se passar onChange custom no `as=`
								    SOBRESCREVE o onChange do Formik e o textarea
								    trava ("não consegue digitar"). Usando field+form no
								    render prop, o Formik continua controlando o valor
								    normalmente e eu consigo adicionar a ação extra de gerar
								    o shortcut oculto sem travar. */}
								<Field name="message">
									{({ field }) => (
										<Textarea
											{...field}
											id="message"
											rows={8}
											autoFocus
											onChange={(e) => {
												// 1) Atualiza o valor do Formik COMO DEVE ser.
												field.onChange(e);
												// 2) Gera o shortcut a partir do começo da
												// mensagem, mas APENAS quando o shortcut
												// atual está vazio/menor que 2 ou é igual ao
												// original (não pisa em valor manual).
												try {
													const curta = gerarShortcut(e.target?.value || "");
													const vals = getValues();
													const atual = (vals?.shortcut || "").trim();
													const original =
														(initialValues?.shortcut ||
															quickAnswer?.shortcut || "")
															.trim();
													if (
														!atual ||
														atual.length < 2 ||
														atual === original
													) {
														setFieldValue("shortcut", curta, false);
													}
												} catch (_) { /* ignora */ }
											}}
											placeholder="Ex.: Atendemos de segunda a sexta, das 8h às 18h."
										/>
									)}
								</Field>
								{touched.message && errors.message && (
									<p className="text-xs text-destructive">{errors.message}</p>
								)}
								<p className="text-xs leading-relaxed text-muted-foreground">
									Nada é enviado sozinho: o texto entra no campo de
									mensagem e a pessoa ainda revisa antes de mandar.
								</p>
							</div>

							<DialogFooter className="flex-col sm:flex-row sm:justify-end sm:space-x-2">
								<Button
									type="button"
									variant="outline"
									onClick={handleClose}
									disabled={isSubmitting}
									className="sm:w-auto w-full"
								>
									{i18n.t("quickAnswersModal.buttons.cancel")}
								</Button>
								{onUse && (
									<Button
										type="submit"
									variant="default"
									disabled={isSubmitting}
									className="sm:w-auto w-full"
									onClick={() => {
										// Garante shortcut antes do submit se ainda não tem.
										try {
											const vals = getValues();
											if (
												!vals ||
												!vals.shortcut ||
												String(vals.shortcut).trim().length < 2
											) {
												const curta = gerarShortcut(vals?.message);
												setFieldValue("shortcut", curta, false);
												vals.shortcut = curta;
											}
										} catch (_) {
											/* ignora */
										}
									}}
									>
										{isSubmitting && (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										)}
										Usar resposta
									</Button>
								)}
							</DialogFooter>
						</Form>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

export default QuickAnswersModal;
